import { isMemberAnAdmin, isMemberAPracticeManager } from "@/core/auth";
import { log } from "@/core/logger";
import { occursOnDate } from "@/lib/recurrence";
import { isBeyondMaxFutureForRole, isBeforeStartOfCurrentMonth } from "@/utils/bookingDateBounds";
import { orgPartyMemberIds } from "@/utils/orgParty";
import type { Booking } from "@/types/Booking";
import type { Location } from "@/types/Location";
import type { Member } from "@/types/Member";
import { addMinutes, isWithinNightClosure } from "@/utils/nightClosure";
import type { Room } from "@/types/Room";
import { endTimeToMinutes } from "@/utils/bookingTime";

type ClickInterceptorArgs = {
  event: React.MouseEvent;
  booking: Booking;
  member: Member;
};

type TimeSlotClickArgs = {
  event: React.MouseEvent;
  date: Date;
  time: string;
  roomId?: string;
  room?: Room | null;
  member: Member;
  bookings?: Booking[] | null;
  location?: Location | null;
  locationId?: string;
  memberId?: string;
};

/**
 * Represents the outcome of a booking slot click interception
 */
export type BookingInterceptionResult = {
  /** The status of the interception */
  status: "success" | "warning" | "declined";
  /** Human-readable message to display to the user */
  message: string;
  /** Whether the member selection should be locked (e.g., when booking from member view page) */
  memberLocked?: boolean;
  details?: {
    /** The date of the attempted booking */
    date?: Date;
    /** The time slot (in HHMM format) */
    time?: string;
    roomId?: string;
    roomName?: string;
    memberId: string;
    member: Member;
    locationId?: string;
    location?: Location;
  };
  /**
   * For "success": Contains booking data needed for the form
   * For "warning": Contains the issues that need user acknowledgment
   * For "declined": Contains the reasons for declination
   */
  data?: Record<string, unknown>;
  /** Bookings for the location/date - used for availability checking in the dialog */
  bookings?: Booking[];
  /** Whether bookings data was available when the interceptor was called (vs null/undefined) */
  bookingsLoaded?: boolean;
};

/* determines based on member status, booking details and adjacency if the booking can be allowed */
const BookingInterceptor = ({ event, booking, member }: ClickInterceptorArgs) => {
  if (!member || !booking) return false;
  if (!member.active) {
    console.warn("Inactive member cannot interact with bookings.");
    return false; // Intercept
  }
  log("BookingInterceptor", event, booking, member);
};

/**
 * Convert time string (HHMM) to minutes since midnight
 */
const timeToMinutes = (time: string): number => {
  const hours = parseInt(time.substring(0, 2), 10);
  const minutes = parseInt(time.substring(2, 4), 10);
  return hours * 60 + minutes;
};

/**
 * Get bookings that OCCUR on a specific date in a specific room.
 *
 * Recurrence-aware (via occursOnDate): a slot/recurring booking whose stored genesis is a
 * different day but which recurs onto `date` is included, matching what the calendar renders.
 * Matching by raw genesis date here was the root cause of overlaps slipping past validation and
 * of phantom clashes on excluded occurrences.
 */
const getBookingsForRoomAndDate = (bookings: Booking[] | null | undefined, roomId: string, date: Date): Booking[] => {
  if (!bookings) return [];
  return bookings.filter((b) => !b.isCancelled && b.roomId === roomId && occursOnDate(b, date));
};

/**
 * Calculate the time range including buffers
 */
const getTimeRangeWithBuffers = (
  startTime: string,
  endTime: string,
  hasBuffers: boolean,
): { start: number; end: number; bufferStart: number; bufferEnd: number } => {
  const BUFFER_MINUTES = 15;
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = endTimeToMinutes(endTime, startTime);

  if (!hasBuffers) {
    return {
      start: startMinutes,
      end: endMinutes,
      bufferStart: startMinutes,
      bufferEnd: endMinutes,
    };
  }

  return {
    start: startMinutes,
    end: endMinutes,
    bufferStart: Math.max(0, startMinutes - BUFFER_MINUTES),
    bufferEnd: Math.min(1440, endMinutes + BUFFER_MINUTES), // 1440 = 24 hours in minutes
  };
};

/* handles clicks on empty time slots to potentially create new bookings */
export const TimeSlotClickInterceptor = ({
  date,
  time,
  roomId,
  room,
  member,
  bookings,
  location,
  locationId,
}: TimeSlotClickArgs): BookingInterceptionResult => {
  const BUFFER_MINUTES = 15;
  const MIN_BOOKING_MINUTES = 30;

  console.log("[TimeSlotClickInterceptor] Received bookings:", {
    bookingsType: typeof bookings,
    bookingsIsNull: bookings === null,
    bookingsIsUndefined: bookings === undefined,
    bookingsIsArray: Array.isArray(bookings),
    bookingsLength: bookings?.length ?? "N/A",
    roomId,
    date: date.toISOString(),
  });

  // Check if room is shuttered at this time
  if (room?.shutterUntil) {
    const shutterDate = new Date(room.shutterUntil);
    const bookingDate = new Date(date);
    const clickedTimeMinutes = timeToMinutes(time);

    // Set the time on the booking date to the clicked time
    bookingDate.setHours(Math.floor(clickedTimeMinutes / 60), clickedTimeMinutes % 60, 0, 0);

    // Check if the booking time is before the shutterUntil time
    if (bookingDate < shutterDate) {
      return {
        status: "declined",
        message: `This room is temporarily closed until ${shutterDate.toLocaleDateString()} at ${shutterDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Please select a different time or room.`,
        details: {
          date,
          time,
          roomId,
          locationId: locationId || location?.id,
          location: location ?? undefined,
          member: member,
          memberId: member.id,
        },
        data: {
          reason: "room_shuttered",
          shutterUntil: room.shutterUntil,
        },
      };
    }
  }

  // R2a/R2b (hearth/docs/BOOKING_RULES.md#r2a) — regular members cannot book in the past;
  // elevated users (admin/PM) can book up to 1 year in the past and no further.
  const isStaffMember = isMemberAnAdmin(member) || isMemberAPracticeManager(member);
  const now = new Date();
  const bookingDate = new Date(date);
  const clickedTimeMinutes = timeToMinutes(time);
  bookingDate.setHours(Math.floor(clickedTimeMinutes / 60), clickedTimeMinutes % 60, 0, 0);

  const declinedDetails = {
    date,
    time,
    roomId,
    locationId: locationId || location?.id,
    location: location ?? undefined,
    member: member,
    memberId: member.id,
  };

  // Nighttime closure — the centre is shut. Checked BEFORE the booking lock and the elevated
  // bypasses below, because unlike every other decline here it applies to EVERYONE: there is no
  // `isStaffMember` escape. The server enforces the same rule (blockIfWithinNightClosure); this is
  // the click intercept so the user gets a clear notice instead of a failed request.
  //
  // A single clicked slot is a start time; treat it as a 30-minute block, matching the calendar's
  // segment granularity. Anything longer can only extend further into the closure, never out of it.
  if (isWithinNightClosure(location?.nightClosure, time, addMinutes(time, 30))) {
    return {
      status: "declined",
      message: "This room is closed over night and is not available at this time.",
      details: declinedDetails,
      data: { reason: "location_night_closure" },
    };
  }

  // Location booking lock — staff can temporarily lock a location so members can still view the
  // calendar (and hover bookings) but cannot create bookings. Elevated users bypass the lock so
  // they can keep making/editing bookings while the location is locked.
  if (!isStaffMember && location?.bookingLocked === true) {
    return {
      status: "declined",
      message: "Booking is temporarily unavailable at this location. Please check back later or contact your practice manager.",
      details: declinedDetails,
      data: { reason: "location_booking_locked" },
    };
  }

  if (!isStaffMember && bookingDate < now) {
    return {
      status: "declined",
      message: "You cannot make bookings in the past. Please select a future date and time.",
      details: declinedDetails,
      data: { reason: "past_booking" },
    };
  }

  if (isStaffMember) {
    if (isBeforeStartOfCurrentMonth(bookingDate, now)) {
      return {
        status: "declined",
        message: "Bookings can't be made before the 1st of this month — last month's billing is closed.",
        details: declinedDetails,
        data: { reason: "past_booking_before_billing_close" },
      };
    }
  }

  // R1 (hearth/docs/BOOKING_RULES.md#r1) — role-aware future cap. Members: 2 calendar
  // months. Elevated users: 52 weeks (365 days). Members get a dedicated decline reason
  // so the CreateBookingDialog can show a "Contact your PM" message instead of the
  // generic unavailable error.
  if (isBeyondMaxFutureForRole(bookingDate, member, now)) {
    if (isStaffMember) {
      return {
        status: "declined",
        message: "Bookings cannot be made more than 52 weeks (1 year) from today.",
        details: declinedDetails,
        data: { reason: "future_booking_beyond_one_year" },
      };
    }
    return {
      status: "declined",
      message:
        "You can only book up to 2 months from today. For bookings further out, please contact your practice manager.",
      details: declinedDetails,
      data: { reason: "future_booking_beyond_member_limit" },
    };
  }

  // Assume a default 30-minute booking duration for validation (`clickedTimeMinutes` declared above
  // for the R2a/R2b past-date check).
  const proposedStartMinutes = clickedTimeMinutes;
  const proposedEndMinutes = clickedTimeMinutes + MIN_BOOKING_MINUTES;
  const proposedBufferStart = proposedStartMinutes - BUFFER_MINUTES;
  const proposedBufferEnd = proposedEndMinutes + BUFFER_MINUTES;

  // Get bookings for this room on this date
  const roomBookings = roomId ? getBookingsForRoomAndDate(bookings, roomId, date) : [];

  // Sort bookings by start time
  const sortedBookings = roomBookings.sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

  // Hoisted above the overlap loop (was previously declared after it) so the elevated buffer_overlap
  // branch below can push a warning instead of declining — see Task 10.
  const warnings: string[] = [];

  // The booked-for member's organisation's own party (its members + owner), if the org is populated
  // on the member. Used to waive the buffer between an org's own back-to-back bookings (mirrors the
  // server). Undefined when there's no org — the buffer check then behaves exactly as before.
  const orgMemberIds = orgPartyMemberIds(member);

  // Check for direct overlaps with existing bookings
  for (const booking of sortedBookings) {
    const bookingRange = getTimeRangeWithBuffers(booking.startTime, booking.endTime, booking.hasBuffers ?? true);

    // Check if the proposed booking's core time overlaps with an existing booking's core time.
    // A real double-booking always blocks, elevated users included — there is no override for this.
    if (proposedStartMinutes < bookingRange.end && proposedEndMinutes > bookingRange.start) {
      return {
        status: "declined",
        message: "This time slot overlaps with an existing booking.",
        details: {
          date,
          time,
          roomId,
          locationId: locationId || location?.id,
          location: location ?? undefined,
          memberId: member.id,
          member: member,
        },
        data: {
          conflictingBooking: booking,
        },
      };
    }

    // Check if the proposed booking's buffers overlap with an existing booking's buffers
    // Exception: Allow buffer overlap when both bookings belong to the SAME PARTY — the same member,
    // OR the same organisation (the booked-for member's org owns the adjacent booking). An org's own
    // back-to-back bookings are one party, so no buffer separates them. Mirrors the server's
    // checkRoomBookingConflict sameOrgMemberIds exemption. Falls back to same-member-only when the
    // member's organisation (with its members/owner) isn't populated — no behaviour change then.
    const isSameMember = booking.memberId === member.id;
    const isSameOrg = orgMemberIds?.has(booking.memberId) ?? false;
    if (!isSameMember && !isSameOrg && proposedBufferStart < bookingRange.bufferEnd && proposedBufferEnd > bookingRange.bufferStart) {
      // Task 10 — an elevated user (admin/PM) may override a cross-member buffer_overlap, but only
      // via the REAL create attempt's 409 (backend Task 5 honours `overrideBufferConflict` there).
      // Declining here, before the dialog even opens, would stop an elevated user ever reaching that
      // 409 — so for elevated users this is downgraded to a warning and the click is allowed through.
      // Non-elevated users keep the existing hard decline (no override for members).
      if (!isStaffMember) {
        return {
          status: "declined",
          message: "This time slot would overlap with another booking's buffer period (15 minutes before/after).",
          details: {
            date,
            time,
            roomId,
            locationId: locationId || location?.id,
            location: location ?? undefined,
            memberId: member.id,
            member: member,
          },
          data: {
            conflictingBooking: booking,
            reason: "buffer_overlap",
          },
        };
      }

      warnings.push(
        "This time slot butts against another member's booking buffer. You can still submit — you'll be asked to confirm before it's booked.",
      );
    }
  }

  // R2b — elevated user booking into the past (but within the billing window).
  // The booking will be created and billed, so surface a warning so the dialog
  // can show a banner rather than silently creating past occurrences.
  if (isStaffMember && bookingDate < now) {
    warnings.push(
      "This date is in the past. The booking will be created in a closed-to-future but open billing month — past occurrences will be created and billed unless you waive billing.",
    );
  }

  for (let i = 0; i < sortedBookings.length; i++) {
    const currentBooking = sortedBookings[i];
    const currentRange = getTimeRangeWithBuffers(
      currentBooking.startTime,
      currentBooking.endTime,
      currentBooking.hasBuffers ?? true,
    );

    // Check gap before the proposed booking
    if (proposedBufferStart > currentRange.bufferEnd) {
      const gapMinutes = proposedBufferStart - currentRange.bufferEnd;
      // An unbookable gap is one that's too small for a minimum booking + buffers (30 + 15 + 15 = 60)
      const minRequiredGap = MIN_BOOKING_MINUTES + 2 * BUFFER_MINUTES;
      if (gapMinutes > 0 && gapMinutes < minRequiredGap) {
        warnings.push(
          `This booking would create a ${gapMinutes}-minute unbookable gap after the ${currentBooking.startTime} booking (minimum ${minRequiredGap} minutes needed).`,
        );
      }
    }

    // Check gap after the proposed booking
    if (proposedBufferEnd < currentRange.bufferStart) {
      const gapMinutes = currentRange.bufferStart - proposedBufferEnd;
      const minRequiredGap = MIN_BOOKING_MINUTES + 2 * BUFFER_MINUTES;
      if (gapMinutes > 0 && gapMinutes < minRequiredGap) {
        warnings.push(
          `This booking would create a ${gapMinutes}-minute unbookable gap before the ${currentBooking.startTime} booking (minimum ${minRequiredGap} minutes needed).`,
        );
      }
    }
  }

  // If there are warnings, return a warning status
  if (warnings.length > 0) {
    return {
      status: "warning",
      message: "This booking may create unbookable time gaps.",
      details: {
        date,
        time,
        roomId,
        locationId: locationId || location?.id,
        location: location ?? undefined,
        memberId: member.id,
        member: member,
      },
      data: {
        warnings,
        suggestedStartTime: time,
        selectedDate: date,
        selectedRoomId: roomId,
      },
      bookings: bookings ?? [],
      bookingsLoaded: Array.isArray(bookings),
    };
  }

  // All checks passed
  log("TimeSlotClickInterceptor", { date, time, roomId, member, room });

  return {
    status: "success",
    message: "This time is available for booking.",
    details: {
      date,
      time,
      roomId,
      locationId: locationId || location?.id,
      location: location ?? undefined,
      memberId: member.id,
      member: member,
    },
    data: {
      // Booking form pre-fill data
      suggestedStartTime: time,
      selectedDate: date,
      selectedRoomId: roomId,
    },
    bookings: bookings ?? [],
    bookingsLoaded: Array.isArray(bookings),
  };
};

export default BookingInterceptor;
